import { useCallback, useEffect, useState } from 'react';
import get from 'lodash/get';
import { useConfirmPopup } from '../../../../hooks/useConfirmPopup';
import {
	ApplicationsFilter,
	ApplicationSummary,
	FunctionaryList,
	PagingConfig,
	UserRole,
} from '../../../../shared/VolunteerApplicationServiceTypes';
import {
	listAvailableFunctionaries,
	reassignFunctionaryToApplication,
} from '../../../../service/VolunteerApplicationService';
import { useToastMessage } from '../../../../hooks/useToastMessage';
import { useLocation } from 'react-router-dom';
import { useSnapshot } from 'valtio';
import { ProposersListState } from './ProposersList.store';
import { DEFAULT_PAGE_SIZE } from '../../../../shared/Constants';
import { SCREENING_DASHBOARD } from '../../../../shared/Routes';
import { useBatchId } from '../../../../hooks/useBatchId';
import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import useUserSession from '../../../../hooks/useUserSession';
import { resetApplicationsListCache } from '../../ApplicationsList/useApplicationsList';

export const useProposerReassignPage = () => {
	const [loading, setLoading] = useState(false);
	const { t } = useTranslation();
	const { navigateToRoute } = useBatchId();
	const { selectedRole } = useUserSession();
	const { confirmPopUpAction } = useConfirmPopup();
	const { showToastMessage } = useToastMessage();
	const [functionaryList, setFunctionaryList] = useState<FunctionaryList>({
		functionary: [],
		pagination: { totalNoOfRecords: 0 },
	});
	const [isFilterTextApplied, setIsFilterTextApplied] =
		useState<boolean>(false);
	const filterStateSnap = useSnapshot(ProposersListState);
	const [paging, setPaging] = useState<PagingConfig>(() => {
		return { ...filterStateSnap.paging } as PagingConfig;
	});
	const [filter, setFilter] = useState<ApplicationsFilter>(() => {
		if (filterStateSnap.filterBy) {
			return { ...filterStateSnap.filterBy } as ApplicationsFilter;
		} else {
			return {};
		}
	});

	const location = useLocation();

	const routeRecord = (
		location.state as {
			applicationRecord?: ApplicationSummary;
		}
	)?.applicationRecord;

	const applicationRecord: ApplicationSummary | null =
		routeRecord ??
		(JSON.parse(
			sessionStorage.getItem('SELECTED_APPLICATION') ?? 'null'
		) as ApplicationSummary | null);

	const onConfirmSubmit = useCallback(
		async (functionaryId: number) => {
			if (functionaryId && applicationRecord?.applicationId) {
				try {
					const response = await reassignFunctionaryToApplication({
						applicationId: applicationRecord?.applicationId,
						functionaryId,
						selectedRoleCode: selectedRole as UserRole,
					});
					if (response) {
						showToastMessage('success', t('proposerReassignedSuccessfully'));
						navigateToRoute(SCREENING_DASHBOARD);
						resetApplicationsListCache();
					}
				} catch (e) {
					if (e instanceof Error) {
						showToastMessage('error', e.message);
					} else {
						showToastMessage('error', 'Unknown error occurred');
					}
				}
			}
		},
		[
			applicationRecord?.applicationId,
			navigateToRoute,
			selectedRole,
			showToastMessage,
			t,
		]
	);

	const onSubmitClick = useCallback(
		(funcId?: number) => {
			if (funcId) {
				confirmPopUpAction(
					'confirmReassign',
					'areYouSureYouWantReassignTheProposer',
					() => onConfirmSubmit(funcId),
					'confirm'
				);
			}
		},
		[onConfirmSubmit, confirmPopUpAction]
	);

	const onPageChange = useCallback((pageNumber: number, pageSize: number) => {
		ProposersListState.paging = { pageNo: pageNumber, pageSize };
		setPaging({ pageNo: pageNumber, pageSize });
	}, []);

	const onFilterChange = useCallback(
		(filterParam: ApplicationsFilter) => {
			const { searchByProposerSrcmId, searchByProposerName, gender } =
				filterParam;
			if (searchByProposerSrcmId || searchByProposerName || gender) {
				setIsFilterTextApplied(true);
			} else {
				setIsFilterTextApplied(false);
			}
			ProposersListState.filterBy = { ...filterParam };
			setFilter(filterParam);
			onPageChange(1, paging.pageSize ?? DEFAULT_PAGE_SIZE);
		},
		[onPageChange, paging.pageSize]
	);

	const fetchProposersList = useCallback(
		async (
			pageNumber: number,
			pageSize: number,
			applicationFilter: ApplicationsFilter
		) => {
			if (applicationRecord?.applicationId) {
				setLoading(true);
				const pageIndex = pageNumber < 1 ? 0 : pageNumber - 1;
				try {
					const response = await listAvailableFunctionaries({
						applicationId: applicationRecord?.applicationId,
						pageIndex,
						pageSize,
						filter: applicationFilter,
					});

					if (
						response.pagination.totalNoOfPages &&
						response.pagination.totalNoOfPages > 0 &&
						pageNumber > response.pagination.totalNoOfPages
					) {
						onPageChange(response.pagination.totalNoOfPages ?? 1, pageSize);
					} else {
						setFunctionaryList(response);
					}
				} catch (e) {
					const errorMsg: string | undefined = get(e, 'response.data.message');

					if (e instanceof AxiosError) {
						// eslint-disable-next-line
						showToastMessage('error', e?.response?.data?.message || e.message);
					} else if (errorMsg) {
						showToastMessage('error', errorMsg);
					} else if (e instanceof Error) {
						showToastMessage('error', e.message);
					}
				} finally {
					setLoading(false);
				}
			}
		},
		[
			onPageChange,
			setLoading,
			showToastMessage,
			applicationRecord?.applicationId,
		]
	);

	useEffect(() => {
		void fetchProposersList(
			paging.pageNo ?? 1,
			paging.pageSize ?? DEFAULT_PAGE_SIZE,
			filter
		);
	}, [fetchProposersList, filter, paging]);

	return {
		onSubmitClick,
		functionaryList,
		paging,
		onPageChange,
		onFilterChange,
		isFilterTextApplied,
		setIsFilterTextApplied,
		filter,
		loading,
	};
};
